import React, { useState, FormEvent, useRef, useEffect, useCallback } from 'react';
import { useStripe, useElements, PaymentElement, Elements } from '@stripe/react-stripe-js';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { API_CONFIG } from '../utils/constants';
import { useTracking } from '../hooks/useTracking';
import { useDeduplicatedPostHog } from '../utils/posthogWrapper';
import { useUser } from '../hooks/useApi';
import { normalizePlanName } from '../utils/planMapping';
import { plans } from '../data/plans';

interface StripeCheckoutFormProps {
  clientSecret: string;
  customerId: string;
  planId: string;
  billingPeriod: string;
  userId?: string;
  onComplete?: () => void;
  onError?: (error: Error) => void;
}

// Inner form component that uses Stripe hooks
function StripeCheckoutForm({ 
  clientSecret, 
  customerId, 
  planId, 
  billingPeriod,
  userId,
  onComplete, 
  onError 
}: StripeCheckoutFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const { track } = useTracking();
  const posthog = useDeduplicatedPostHog(); // Use deduplicated wrapper
  const { user: userData } = useUser(); // Get user data to check previous subscription status
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const propertiesSetRef = useRef(false); // Track if properties were set for this transaction
  const [paymentElementReady, setPaymentElementReady] = useState(false);
  const paymentElementMountTimeRef = useRef<number>(Date.now());

  // Store user's subscription status BEFORE payment attempt to check if this is initial purchase
  // This prevents race conditions where user data might update after payment succeeds
  const previousSubscriptionStatusRef = useRef<string | null | undefined>(null);
  
  // Capture user's subscription status when component mounts or user data loads
  useEffect(() => {
    if (userData) {
      previousSubscriptionStatusRef.current = userData.subscription_status;
      console.log('[EmbeddedCheckout] Captured previous subscription status:', previousSubscriptionStatusRef.current);
    }
  }, [userData]); // Update when userData changes (subscription_status is part of userData)

  // Track when PaymentElement successfully mounts
  const handlePaymentElementReady = useCallback(() => {
    const mountDuration = Date.now() - paymentElementMountTimeRef.current;
    setPaymentElementReady(true);
    
    track('payment_element_ready', {
      component: 'EmbeddedCheckout',
      plan_id: planId,
      billing_period: billingPeriod,
      mount_duration_ms: mountDuration,
    });
    
    console.log(`✅ [EmbeddedCheckout] PaymentElement ready in ${mountDuration}ms`);
  }, [track, planId, billingPeriod]);

  // Track PaymentElement errors and completion
  const handlePaymentElementChange = useCallback((event: any) => {
    if (event.error) {
      console.error('❌ [EmbeddedCheckout] PaymentElement error:', event.error);

      track('payment_element_error', {
        component: 'EmbeddedCheckout',
        error_type: event.error.type,
        error_code: event.error.code,
        error_message: event.error.message,
        plan_id: planId,
        billing_period: billingPeriod,
      });
    }

    if (event.complete) {
      track('payment_element_complete', {
        component: 'EmbeddedCheckout',
        plan_id: planId,
        billing_period: billingPeriod,
      });
    }
  }, [track, planId, billingPeriod]);

  // Timeout detection: if PaymentElement doesn't become ready within 10 seconds, show error
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!paymentElementReady) {
        const waitTime = Date.now() - paymentElementMountTimeRef.current;
        
        console.error(`❌ [EmbeddedCheckout] PaymentElement timeout after ${waitTime}ms`);
        
        track('payment_element_timeout', {
          component: 'EmbeddedCheckout',
          plan_id: planId,
          billing_period: billingPeriod,
          wait_time_ms: waitTime,
          stripe_loaded: !!stripe,
          elements_loaded: !!elements,
        });
        
        setErrorMessage(
          'Payment form is taking longer than expected to load. ' +
          'Please refresh the page and try again. If the problem persists, contact support.'
        );
        
        if (onError) {
          onError(new Error('PaymentElement mount timeout'));
        }
      }
    }, 10000); // 10 second timeout

    return () => clearTimeout(timeoutId);
  }, [paymentElementReady, stripe, elements, track, planId, billingPeriod, onError]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);

    try {
      // Submit payment form to Stripe
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setErrorMessage(submitError.message || 'Payment form validation failed');
        setIsProcessing(false);
        return;
      }

      // Confirm SetupIntent to collect payment method
      const { error: confirmError, setupIntent } = await stripe.confirmSetup({
        elements,
        clientSecret,
        redirect: 'if_required', // Only redirect if required (e.g., 3D Secure)
      });

      if (confirmError) {
        setErrorMessage(confirmError.message || 'Payment method collection failed');
        if (onError) {
          onError(new Error(confirmError.message || 'Payment method collection failed'));
        }
        setIsProcessing(false);
        return;
      }

      if (!setupIntent || !setupIntent.payment_method) {
        throw new Error('Payment method not collected');
      }

      // Payment method collected, now create subscription
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/stripe/create-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customer_id: customerId,
          payment_method_id: setupIntent.payment_method,
          plan_id: planId,
          billing_period: billingPeriod,
          user_id: userId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to create subscription');
      }

      const data = await response.json();
      
      if (data.success) {
        // ===== CRITICAL: Verify Payment Actually Succeeded =====
        // Backend might return success but payment could still fail
        // Check multiple sources for payment status to be thorough
        
        const subscriptionStatus = data.status || data.subscription?.status || data.subscription_status;
        const paymentStatus = data.payment_intent?.status || data.payment_status;
        const invoiceStatus = data.latest_invoice?.payment_intent?.status || data.invoice?.payment_intent?.status;
        const subscriptionId = data.subscription_id || data.subscription?.id || data.transaction_id;
        
        // Payment is considered successful if:
        // 1. Subscription status is 'active' or 'trialing' (trialing means payment succeeded but in trial period)
        // 2. Payment intent status is 'succeeded' (if available)
        // 3. Invoice payment intent status is 'succeeded' (if available)
        // If none of these are available, we assume success if subscription_status is active/trialing
        const subscriptionIsActive = subscriptionStatus === 'active' || subscriptionStatus === 'trialing';
        const paymentIntentSucceeded = paymentStatus === 'succeeded' || invoiceStatus === 'succeeded';
        
        // If we have payment intent info, it must be succeeded
        // If we don't have payment intent info, rely on subscription status
        const hasPaymentInfo = paymentStatus || invoiceStatus;
        const paymentSucceeded = hasPaymentInfo 
          ? (paymentIntentSucceeded && subscriptionIsActive)
          : subscriptionIsActive; // Fallback: if no payment info, trust subscription status
        
        if (!paymentSucceeded) {
          // Payment verification failed - track failure event and throw error
          console.error('[EmbeddedCheckout] Payment verification failed:', {
            subscriptionStatus,
            paymentStatus,
            invoiceStatus,
            subscriptionId,
          });
          
          track('payment_failed', {
            plan_id: planId,
            billing_period: billingPeriod,
            subscription_id: subscriptionId,
            subscription_status: subscriptionStatus || 'unknown',
            payment_status: paymentStatus || 'unknown',
            invoice_status: invoiceStatus || 'unknown',
            error_type: 'payment_verification_failed',
            backend_success: true, // Backend said success but payment didn't actually succeed
            user_id: userId || '',
            timestamp: new Date().toISOString(),
          });
          
          throw new Error('Payment verification failed. Please try again or contact support if the issue persists.');
        }
        
        // ===== CRITICAL: Check if This is Initial Subscription Purchase =====
        // Only track 'subscribe' event for INITIAL purchases, not renewals/updates/upgrades/downgrades
        // Note: If user immediately cancels after subscribing, the subscribe event should still fire
        // because the purchase DID happen - cancellation is a separate action after purchase
        const previousSubscriptionStatus = previousSubscriptionStatusRef.current;
        
        // User had a previous subscription if status was 'active', 'canceling', or 'trialing'
        // 'canceled' or null means no previous subscription (new purchase)
        // This correctly handles:
        // - Plan upgrades/downgrades (had 'active' subscription) → subscription_updated
        // - Renewals (had 'active' subscription) → subscription_updated
        // - New subscriptions after cancellation (had 'canceled' or null) → subscribe
        // - Immediate cancellation after purchase (no previous subscription) → subscribe ✅
        const hadPreviousSubscription = 
          previousSubscriptionStatus === 'active' || 
          previousSubscriptionStatus === 'canceling' ||
          previousSubscriptionStatus === 'trialing';
        
        if (hadPreviousSubscription) {
          // User already had an active subscription - this is a renewal/upgrade/downgrade, not initial purchase
          
          // Get old plan from user data (normalize to handle 'starter'/'ultimate' from database)
          const oldPlanId = userData?.subscription_tier ? normalizePlanName(userData.subscription_tier) : null;
          const newPlanId = normalizePlanName(planId);
          
          // Determine if this is an upgrade or downgrade
          // Plan hierarchy: basic < pro < premium
          const planHierarchy: Record<string, number> = {
            'basic': 1,
            'pro': 2,
            'premium': 3,
          };
          
          const oldPlanRank = oldPlanId ? (planHierarchy[oldPlanId] || 0) : 0;
          const newPlanRank = planHierarchy[newPlanId] || 0;
          
          // Get plan data for prices and credits
          const oldPlanData = oldPlanId ? plans.find(p => p.id === oldPlanId) : null;
          const newPlanData = plans.find(p => p.id === newPlanId);
          
          // Calculate price differences (using monthly prices from plans data)
          const oldPlanPrice = oldPlanData?.price || 0;
          const newPlanPrice = newPlanData?.price || 0;
          const priceDifference = newPlanPrice - oldPlanPrice;
          
          // Calculate credit differences
          const oldPlanCredits = oldPlanData?.credits || 0;
          const newPlanCredits = newPlanData?.credits || 0;
          const creditDifference = newPlanCredits - oldPlanCredits;
          
          // Determine event type: upgrade, downgrade, or renewal (same plan)
          let eventType: 'upgrade' | 'downgrade' | 'renewal';
          if (newPlanRank > oldPlanRank) {
            eventType = 'upgrade';
          } else if (newPlanRank < oldPlanRank) {
            eventType = 'downgrade';
          } else {
            eventType = 'renewal'; // Same plan, just renewal
          }
          
          console.log('[EmbeddedCheckout] Subscription change detected:', {
            old_plan: oldPlanId,
            new_plan: newPlanId,
            old_rank: oldPlanRank,
            new_rank: newPlanRank,
            event_type: eventType,
            subscription_id: subscriptionId,
          });
          
          // Track upgrade or downgrade event (not renewal - renewal is just subscription_updated)
          if (eventType === 'upgrade') {
            track('upgrade', {
              transaction_id: subscriptionId || `sub_${Date.now()}`,
              old_plan: oldPlanId || 'unknown',
              new_plan: newPlanId,
              old_plan_name: oldPlanData?.name || oldPlanId || 'unknown',
              new_plan_name: newPlanData?.name || newPlanId,
              old_plan_price: oldPlanPrice,
              new_plan_price: newPlanPrice,
              price_difference: priceDifference, // Positive value for upgrade
              old_plan_credits: oldPlanCredits,
              new_plan_credits: newPlanCredits,
              credit_difference: creditDifference, // Positive value for upgrade
              billing_period: billingPeriod,
              currency: 'USD',
              value: Math.abs(priceDifference), // Conversion value = absolute price difference
              product_type: 'subscription',
              user_id: userId || '',
              subscription_id: subscriptionId,
              old_subscription_status: previousSubscriptionStatus,
              new_subscription_status: subscriptionStatus,
              timestamp: new Date().toISOString(),
            });
          } else if (eventType === 'downgrade') {
            track('downgrade', {
              transaction_id: subscriptionId || `sub_${Date.now()}`,
              old_plan: oldPlanId || 'unknown',
              new_plan: newPlanId,
              old_plan_name: oldPlanData?.name || oldPlanId || 'unknown',
              new_plan_name: newPlanData?.name || newPlanId,
              old_plan_price: oldPlanPrice,
              new_plan_price: newPlanPrice,
              price_difference: priceDifference, // Negative value for downgrade
              old_plan_credits: oldPlanCredits,
              new_plan_credits: newPlanCredits,
              credit_difference: creditDifference, // Negative value for downgrade
              billing_period: billingPeriod,
              currency: 'USD',
              value: Math.abs(priceDifference), // Conversion value = absolute price difference
              product_type: 'subscription',
              user_id: userId || '',
              subscription_id: subscriptionId,
              old_subscription_status: previousSubscriptionStatus,
              new_subscription_status: subscriptionStatus,
              timestamp: new Date().toISOString(),
            });
          } else {
            // Same plan - just renewal, track as subscription_updated
            track('subscription_updated', {
              transaction_id: subscriptionId || `sub_${Date.now()}`,
              plan_id: planId,
              plan_name: planId,
              currency: 'USD',
              billing_period: billingPeriod,
              previous_status: previousSubscriptionStatus,
              new_status: subscriptionStatus,
              product_type: 'subscription',
              user_id: userId || '',
              timestamp: new Date().toISOString(),
            });
          }
        } else {
          // This is an INITIAL subscription purchase - track subscribe event
          // This includes:
          // - First-time subscribers (no previous subscription)
          // - Users who canceled and are subscribing again (previous status was 'canceled' or null)
          // - Users who subscribe and immediately cancel (cancellation happens after purchase, so subscribe event fires) ✅
          console.log('[EmbeddedCheckout] Tracking subscribe event - initial subscription purchase:', {
            previous_status: previousSubscriptionStatus || 'none',
            new_status: subscriptionStatus,
            subscription_id: subscriptionId,
            payment_status: paymentStatus || invoiceStatus || 'verified',
            note: 'Subscribe event fires even if user cancels immediately after (purchase did happen)',
          });
          
          // Hardcoded conversion values based on plan type (same for monthly and annual)
          const conversionValuesByPlan: Record<string, number> = {
            'basic': 20,
            'pro': 40,
            'premium': 80,
          };
          const conversionValue = conversionValuesByPlan[planId.toLowerCase()] || 0;
          
          track('subscribe', {
            transaction_id: subscriptionId || `sub_${Date.now()}`,
            plan_id: planId,
            plan_name: planId, // Could be enhanced to get plan name
            currency: 'USD',
            value: conversionValue, // Hardcoded: Basic=$20, Pro=$40, Premium=$80
            billing_period: billingPeriod,
            product_type: 'subscription', // Distinguish from credit packs
            user_id: userId || '',
            subscription_status: subscriptionStatus, // Include status for verification
            payment_status: paymentStatus || invoiceStatus || 'succeeded', // Include payment status
            timestamp: new Date().toISOString(),
          });
        }

        // Update user properties with payment info (deduplicated via wrapper)
        // This happens for both initial purchases and renewals
        if (posthog && userId && !propertiesSetRef.current) {
          posthog.setPersonProperties({
            has_payment_method: true,
            subscription_plan: planId,
            subscription_billing_period: billingPeriod,
            subscription_status: subscriptionStatus || 'active',
            last_purchase_date: new Date().toISOString(),
            payment_method_type: 'card',
          });
          propertiesSetRef.current = true; // Mark as set to prevent duplicates in this transaction
        }
        
        // Subscription created successfully and payment verified
        if (onComplete) {
          onComplete();
        }
      } else {
        // Backend returned explicit failure
        const errorMessage = data.error || data.message || 'Subscription creation failed';
        
        // Track payment failure event
        track('payment_failed', {
          plan_id: planId,
          billing_period: billingPeriod,
          subscription_id: data.subscription_id || null,
          error_type: 'backend_failure',
          error_message: errorMessage,
          backend_success: false,
          user_id: userId || '',
          timestamp: new Date().toISOString(),
        });
        
        throw new Error(errorMessage);
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('An unexpected error occurred');
      
      // If error is not already tracked (payment_failed), track it as generic error
      // This handles network errors, unexpected failures, etc.
      if (!error.message.includes('Payment verification failed') && 
          !error.message.includes('Subscription creation failed')) {
        track('subscription_error', {
          plan_id: planId,
          billing_period: billingPeriod,
          error_type: 'unexpected_error',
          error_message: error.message,
          user_id: userId || '',
          timestamp: new Date().toISOString(),
        });
      }
      
      setErrorMessage(error.message);
      if (onError) {
        onError(error);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
      {/* Card Payment Form - Express Checkout (Apple Pay / Google Pay / Link) is on plan cards only */}
      <div className="bg-white/5 border border-white/20 rounded-lg p-3 sm:p-4 relative">
        {!paymentElementReady && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/5 rounded-lg">
            <div className="text-center space-y-2">
              <div className="animate-pulse text-white/70">Loading payment form...</div>
              <div className="text-xs text-white/50">Please wait</div>
            </div>
          </div>
        )}

        <PaymentElement
          onReady={handlePaymentElementReady}
          onChange={handlePaymentElementChange}
          options={{
            layout: 'tabs',
            // Disable all wallets - Express checkout (Apple Pay, Google Pay, Link) is only on plan cards
            wallets: { applePay: 'never', googlePay: 'never' },
          }}
        />
      </div>

      {errorMessage && (
        <div className="p-3 sm:p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
          {errorMessage}
        </div>
      )}

      <div className="sticky bottom-0 bg-bg pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom,0.5rem))] sm:pb-[calc(1rem+env(safe-area-inset-bottom,1rem))]">
        <button
          type="submit"
          disabled={!stripe || isProcessing}
          className="w-full bg-[#F9F7EA] text-[#0f1218] px-6 py-3 rounded-lg font-semibold hover:bg-[#E7D7B1] transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
          data-testid="subscribe-button"
        >
          {isProcessing ? 'Processing...' : 'Subscribe Now'}
        </button>
      </div>
    </form>
  );
}

// Main component that wraps with Elements provider
interface EmbeddedCheckoutProps {
  clientSecret: string;
  customerId: string;
  planId: string;
  billingPeriod: string;
  publishableKey?: string; // Optional: if provided, use this key instead of fetching
  userId?: string;
  onComplete?: () => void;
  onError?: (error: Error) => void;
}

export default function EmbeddedCheckoutComponent({ 
  clientSecret,
  customerId,
  planId,
  billingPeriod,
  publishableKey,
  userId,
  onComplete,
  onError 
}: EmbeddedCheckoutProps) {
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { track } = useTracking();

  useEffect(() => {
    const initializeStripe = async () => {
      const startTime = Date.now();
      let stage = 'started';
      
      try {
        setLoading(true);
        setError(null);

        // Track initialization start
        track('stripe_init_started', {
          component: 'EmbeddedCheckout',
          plan_id: planId,
          billing_period: billingPeriod,
          timestamp: new Date().toISOString(),
        });

        stage = 'fetching_key';
        let key = publishableKey;

        // If no key provided, fetch from backend config endpoint
        if (!key) {
          try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout for API call
            
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/stripe/config`, {
              signal: controller.signal,
            });
            clearTimeout(timeoutId);
            
            const data = await response.json();
            
            if (data.success && data.publishable_key) {
              const fetchedKey = data.publishable_key;
              key = fetchedKey;
              
              track('stripe_key_fetched', { 
                duration_ms: Date.now() - startTime,
              });
            } else {
              console.error('[EmbeddedCheckout] Invalid config response:', data);
              throw new Error('Invalid response from config endpoint');
            }
          } catch (configError) {
            const errorMsg = configError instanceof Error ? configError.message : 'Unknown error';
            console.error('[EmbeddedCheckout] Failed to fetch config:', configError);
            
            track('stripe_key_fetch_failed', { 
              error: errorMsg,
              duration_ms: Date.now() - startTime,
            });
            
            throw new Error('Failed to get Stripe configuration from backend');
          }
        }

        if (!key) {
          throw new Error('Stripe publishable key is required');
        }

        // Verify we're using the correct key type
        stage = 'validating_key';
        const isTestKey = key.startsWith('pk_test_');
        const isLiveKey = key.startsWith('pk_live_');
        const hostname = window.location.hostname;
        const isDevEnvironment = hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('dev') || API_CONFIG.BASE_URL.includes('devapi');
        
        // For localhost/dev, we MUST use test key - REJECT live keys immediately
        if (isDevEnvironment) {
          if (isLiveKey) {
            const errorMsg = 'Live Stripe key detected in development environment. Backend should return test key.';
            console.error('[EmbeddedCheckout]', errorMsg);
            setError('Backend returned incorrect Stripe key for development. Please contact support.');
            throw new Error(errorMsg);
          }
          if (!isTestKey) {
            const errorMsg = `Invalid Stripe key format. Expected pk_test_... but got: ${key.substring(0, 30)}...`;
            console.error('[EmbeddedCheckout]', errorMsg);
            setError('Invalid Stripe configuration. Please contact support.');
            throw new Error(errorMsg);
          }
        }
        
        // Initialize Stripe with timeout protection
        stage = 'loading_stripe_js';
        track('stripe_js_loading_started', { 
          duration_ms: Date.now() - startTime,
        });
        
        const stripeLoadPromise = loadStripe(key);
        const timeoutPromise = new Promise<null>((_, reject) => {
          setTimeout(() => reject(new Error('Stripe.js load timeout after 15 seconds')), 15000);
        });
        
        const stripe = await Promise.race([stripeLoadPromise, timeoutPromise]);
        
        if (!stripe) {
          throw new Error('Stripe.js failed to load (null returned)');
        }
        
        const totalDuration = Date.now() - startTime;
        track('stripe_js_loaded_success', { 
          duration_ms: totalDuration,
          plan_id: planId,
          billing_period: billingPeriod,
        });
        
        setStripePromise(Promise.resolve(stripe));
        
      } catch (err) {
        const duration = Date.now() - startTime;
        const errorMessage = err instanceof Error ? err.message : 'Failed to initialize Stripe';
        
        // Detect specific browser/environment issues
        const browserInfo = {
          user_agent: navigator.userAgent,
          is_facebook_browser: navigator.userAgent.includes('FBAN') || navigator.userAgent.includes('FBAV'),
          is_android: /Android/i.test(navigator.userAgent),
          is_chrome: /Chrome/i.test(navigator.userAgent),
          is_ios: /iPhone|iPad|iPod/i.test(navigator.userAgent),
          is_safari: /Safari/i.test(navigator.userAgent) && !/Chrome/i.test(navigator.userAgent),
          network_type: (navigator as any).connection?.effectiveType || 'unknown',
          downlink: (navigator as any).connection?.downlink || 'unknown',
        };
        
        console.error('❌ [EmbeddedCheckout] Stripe initialization error:', err);
        console.error('❌ [EmbeddedCheckout] Browser info:', browserInfo);
        
        track('stripe_init_failed', {
          stage,
          error: errorMessage,
          duration_ms: duration,
          plan_id: planId,
          billing_period: billingPeriod,
          ...browserInfo,
        });
        
        // User-friendly error messages based on the failure stage
        let userMessage = 'Failed to load payment form. ';
        if (stage === 'loading_stripe_js') {
          userMessage += 'This may be due to a slow network connection. Please refresh the page and try again.';
        } else if (stage === 'fetching_key') {
          userMessage += 'Unable to connect to payment service. Please check your internet connection and try again.';
        } else {
          userMessage += 'Please refresh the page and try again.';
        }
        
        setError(userMessage);
        if (onError) {
          onError(new Error(errorMessage));
        }
      } finally {
        setLoading(false);
      }
    };

    initializeStripe();
  }, [publishableKey, onError, track, planId, billingPeriod]);

  if (loading) {
    return (
      <div className="p-4 bg-white/5 border border-white/20 rounded-lg text-white/70 text-center space-y-2">
        <div className="animate-pulse">Initializing payment form...</div>
        <div className="text-xs text-white/50">This may take a few seconds on mobile networks</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400">
        {error}
      </div>
    );
  }

  if (!stripePromise) {
    return (
      <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400">
        Stripe is not configured. Please check your backend connection.
      </div>
    );
  }

  return (
    <div className="w-full" id="embedded-checkout">
      <Elements
        stripe={stripePromise}
        options={{
          clientSecret,
          appearance: {
            theme: 'night',
            variables: {
              colorPrimary: '#ffffff',
              colorBackground: '#000000',
              colorText: '#ffffff',
              colorDanger: '#ef4444',
              fontFamily: 'system-ui, sans-serif',
              spacingUnit: '4px',
              borderRadius: '8px',
            },
          },
        }}
      >
        <StripeCheckoutForm 
          clientSecret={clientSecret}
          customerId={customerId}
          planId={planId}
          billingPeriod={billingPeriod}
          userId={userId}
          onComplete={onComplete} 
          onError={onError} 
        />
      </Elements>
    </div>
  );
}
